import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import Seo from "@/components/Seo";
import { Send, BookOpen, Sparkles, FileText, ArrowRight, Lock, MessageCircle, ShieldCheck, Languages } from "lucide-react";
import { toast } from "sonner";

/**
 * /demo — zero-friction public sandbox.
 * Visitors get a pre-loaded NEET Biology PDF and 7 free questions before being soft-gated
 * to /register. A soft signup nudge appears once the visitor has asked 5 questions (still
 * lets them keep asking). No auth. No localStorage shenanigans — the session id is the
 * source of truth.
 *
 * Reuses the SPEC→SENSE→SEE→CHECK pedagogy + follow-up chips + citations + general-knowledge
 * affordance from the main chat. Different visual chrome so visitors immediately understand
 * this is a guided sample, not the real product.
 */

function DemoTypingDots() {
  return (
    <span className="inline-flex items-center gap-1.5" aria-label="Tutor is typing">
      <span className="tutor-dot" />
      <span className="tutor-dot" />
      <span className="tutor-dot" />
    </span>
  );
}

const SAMPLE_QUESTIONS = [
  "Why are mitochondria called the powerhouse of the cell?",
  "What's the difference between prokaryotic and eukaryotic cells?",
  "Explain the fluid mosaic model of the plasma membrane.",
  "What is the endosymbiotic theory?",
  "Describe the structure and function of the Golgi apparatus.",
  "Why are lysosomes called suicidal bags of the cell?",
];

export default function Demo() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [session, setSession] = useState(null);
  const [doc, setDoc] = useState(null);
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [capReached, setCapReached] = useState(false);
  // 2026-07: Soft signup nudge triggers once the visitor asks their 5th question.
  // Purely informational — user can dismiss and keep asking until the hard cap at Q7.
  const [softPromptDismissed, setSoftPromptDismissed] = useState(false);
  const scrollerRef = useRef(null);
  const textareaRef = useRef(null);

  // Start a fresh demo session on first mount. Topic param is a placeholder for future
  // pre-seeded sample swaps (cell-structure / newton / etc.).
  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.post("/public/demo/start", {
          topic: searchParams.get("topic") || "cell-structure",
          language: "English",
        });
        setSession(data.session);
        setDoc(data.doc);
        setMessages(data.messages || []);
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    scrollerRef.current?.scrollTo({ top: scrollerRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, sending]);

  const submitMessage = async (q, opts = {}) => {
    if (!session || sending || capReached) return;
    const optimistic = { msg_id: `tmp_${Date.now()}`, role: "user", content: q };
    setMessages((m) => [...m, optimistic]);
    setText("");
    setSending(true);
    try {
      const { data } = await http.post(`/public/demo/${session.session_id}/message`, {
        text: q,
        allow_general_knowledge: !!opts.allowGeneralKnowledge,
      });
      setMessages((m) => [...m.filter((x) => x.msg_id !== optimistic.msg_id), data.user_message, data.assistant_message]);
      if (data.cap_reached_next) setCapReached(true);
    } catch (e) {
      if (e?.response?.status === 402) {
        setCapReached(true);
        setMessages((m) => m.filter((x) => x.msg_id !== optimistic.msg_id));
      } else {
        toast.error(formatApiError(e));
        setMessages((m) => m.filter((x) => x.msg_id !== optimistic.msg_id));
      }
    } finally {
      setSending(false);
    }
  };

  const retryWithGK = async () => {
    const lastUser = [...messages].reverse().find((m) => m.role === "user");
    if (!lastUser || sending) return;
    await submitMessage(lastUser.content, { allowGeneralKnowledge: true });
  };

  const send = (e) => {
    e?.preventDefault?.();
    const q = text.trim();
    if (!q) return;
    submitMessage(q);
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const onTextChange = (e) => {
    setText(e.target.value);
    const ta = textareaRef.current;
    if (ta) {
      ta.style.height = "auto";
      ta.style.height = Math.min(ta.scrollHeight, 140) + "px";
    }
  };

  const userMessageCount = messages.filter((m) => m.role === "user").length;
  const askedCount = session?.message_count ?? userMessageCount;
  const remaining = session ? Math.max(0, session.cap - askedCount) : 7;
  // Soft prompt appears once user has asked >=5 questions but hasn't hit the hard cap.
  const showSoftPrompt = !capReached && askedCount >= 5 && askedCount < (session?.cap ?? 7) && !softPromptDismissed;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="demo-page">
      <Seo
        title="Try the AI Tutor — Free Demo with NEET Biology Sample | VidyaAI Study"
        description="Try VidyaAI Study before signing up. Ask 7 free questions on a NEET Biology sample chapter and see the AI tutor answer in English, Hindi, Tamil or any Indian language."
        path="/demo"
      />
      {/* Visually hidden H1 for SEO / accessibility. The sticky header above uses smaller
          text by design, but crawlers (and screen readers) need a single descriptive H1
          per the Semrush "Missing h1" audit finding — fixed Feb 2026 SEO lockdown. */}
      <h1 className="sr-only">VidyaAI Study — Try the AI Tutor on a NEET Biology sample chapter</h1>
      {/* slim header */}
      <header className="border-b border-[#E6E2D8] bg-white/80 backdrop-blur sticky top-0 z-20">
        <div className="container-x py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link to="/" className="flex items-center gap-2.5" data-testid="demo-home-link">
            <img src="/logo-mark.png" alt="VidyaAI Study" className="w-8 h-8 rounded-lg object-contain" />
            <div>
              <div className="font-display text-base leading-none tracking-tight">VidyaAI Study</div>
              <div className="text-[10px] uppercase tracking-[0.15em] text-[#7A7369]">Try-before-signup demo</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <span className="text-xs text-[#7A7369] hidden sm:inline" data-testid="demo-questions-remaining">
              {remaining} of {session?.cap ?? 7} free questions left
            </span>
            <Link to="/register?role=self_study_student" className="btn-primary text-sm" data-testid="demo-cta-signup">
              Get full access <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </header>

      <div className="container-x py-6 sm:py-10 grid lg:grid-cols-12 gap-6">
        {/* Doc panel — left */}
        <aside className="lg:col-span-4 space-y-4">
          <div className="card-surface" data-testid="demo-doc-card">
            <div className="label-eyebrow mb-2">Sample material loaded</div>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#FCE8E2] grid place-items-center shrink-0">
                <FileText size={18} className="text-[#C24933]" strokeWidth={1.6} />
              </div>
              <div className="min-w-0">
                <div className="font-display text-base tracking-tight leading-tight">
                  {doc?.title || "NEET Biology — Cell: The Unit of Life"}
                </div>
                <div className="text-xs text-[#7A7369] mt-1">{doc?.page_count || 9} pages · Pre-loaded for this demo</div>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-[#E6E2D8] text-sm text-[#4A4640]">
              Ask anything from this chapter. Try Hindi, English, or Hinglish — the tutor will mirror your language.
            </div>
            <div
              className="mt-3 text-sm text-[#C24933] leading-relaxed"
              data-testid="demo-own-notes-nudge"
            >
              This is exactly how your own notes will work — upload your PDF and your students get answers instantly.
            </div>
          </div>

          <div className="card-surface bg-[#F4F1EB] border-[#E6E2D8]" data-testid="demo-why">
            <div className="label-eyebrow mb-3">What makes this different</div>
            <ul className="space-y-2.5 text-sm text-[#4A4640]">
              <li className="flex items-start gap-2.5"><ShieldCheck size={14} className="mt-0.5 shrink-0 text-[#4A6B53]" strokeWidth={1.8} /><span>Answers <strong>only</strong> from the loaded chapter — no hallucinations.</span></li>
              <li className="flex items-start gap-2.5"><Languages size={14} className="mt-0.5 shrink-0 text-[#4A6B53]" strokeWidth={1.8} /><span>Reply in your language — Hindi, Hinglish, Tamil, Marathi, more.</span></li>
              <li className="flex items-start gap-2.5"><BookOpen size={14} className="mt-0.5 shrink-0 text-[#4A6B53]" strokeWidth={1.8} /><span>Every answer cites the exact page.</span></li>
              <li className="flex items-start gap-2.5"><MessageCircle size={14} className="mt-0.5 shrink-0 text-[#4A6B53]" strokeWidth={1.8} /><span>Same tutor works on Telegram once you sign up.</span></li>
            </ul>
          </div>
        </aside>

        {/* Chat panel — right */}
        <main className="lg:col-span-8">
          <div className="card-surface !p-0 flex flex-col h-[calc(100vh-160px)] min-h-[520px]" data-testid="demo-chat">
            <div className="px-5 py-4 border-b border-[#E6E2D8]">
              <div className="font-display text-lg tracking-tight">Ask your first question</div>
              <p className="text-xs text-[#7A7369] mt-0.5">7 free questions. Then create a free account to bring your own notes.</p>
            </div>

            <div ref={scrollerRef} className="flex-1 overflow-auto px-4 sm:px-6 py-5 sm:py-6 space-y-4">
              {messages.length === 0 && (
                <div className="text-center text-[#7A7369] py-8" data-testid="demo-loading">
                  <Sparkles size={18} className="mx-auto mb-2 text-[#C24933]" />
                  <div className="text-sm">Loading sample chapter…</div>
                </div>
              )}
              {messages.map((m) => (
                <div key={m.msg_id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[88%] sm:max-w-[78%] px-4 py-3 rounded-2xl whitespace-pre-wrap text-sm leading-relaxed ${
                      m.role === "user" ? "bg-[#C24933] text-[#FDFBF7] rounded-br-md" : "bg-[#F4F1EB] text-[#1A1713] rounded-bl-md"
                    }`}
                    data-testid={`demo-msg-${m.role}`}
                  >
                    {m.content}

                    {m.role === "assistant" && Array.isArray(m.chunk_citations) && m.chunk_citations.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-[#E6E2D8]">
                        <div className="text-[10px] text-[#7A7369] uppercase tracking-wider mb-1.5">Source</div>
                        <div className="flex flex-wrap gap-1.5">
                          {m.chunk_citations.map((c, i) => {
                            const pageLabel = c.page_start && c.page_end
                              ? (c.page_start === c.page_end ? `p. ${c.page_start}` : `p. ${c.page_start}–${c.page_end}`)
                              : null;
                            return (
                              <span
                                key={c.chunk_id || i}
                                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-white border border-[#E6E2D8] text-[10px] text-[#4A4640]"
                                title={c.snippet}
                              >
                                <FileText size={9} className="text-[#C24933]" />
                                <span className="font-medium max-w-[120px] truncate">{c.title}</span>
                                {pageLabel && <span className="text-[#7A7369]">· {pageLabel}</span>}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {m.role === "assistant" && m.refused && !sending && !capReached && (
                      <div className="mt-3 pt-3 border-t border-[#E6E2D8] space-y-2">
                        <div className="text-[11px] text-[#7A7369]">Not in the sample notes. Want me to answer from general knowledge?</div>
                        <div className="flex gap-2">
                          <button type="button" onClick={retryWithGK} className="text-[11px] px-3 py-1.5 rounded-full bg-[#C24933] text-white hover:bg-[#A93D29]" data-testid="demo-gk-yes">Answer From GK</button>
                          <button type="button" onClick={() => toast.info("OK — try another question from the chapter.")} className="text-[11px] px-3 py-1.5 rounded-full bg-white border border-[#E6E2D8] text-[#4A4640]" data-testid="demo-gk-no">No, skip</button>
                        </div>
                      </div>
                    )}

                    {m.role === "assistant" && m.general_knowledge && (
                      <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-[#7A5A14] font-medium">
                        <Sparkles size={10} className="text-[#E5A93A]" /> Answered from general knowledge
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start" data-testid="demo-typing">
                  <div className="px-4 py-3 rounded-2xl bg-[#F4F1EB] text-[#7A7369] text-sm">
                    <DemoTypingDots />
                  </div>
                </div>
              )}

              {/* follow-up chips on last assistant message */}
              {!sending && text.trim().length === 0 && !capReached && (() => {
                const last = [...messages].reverse().find((m) => m.role === "assistant");
                const followups = last?.followups || [];
                if (followups.length === 0) return null;
                return (
                  <div className="flex justify-start" data-testid="demo-followups">
                    <div className="max-w-[88%] sm:max-w-[78%]">
                      <div className="text-[10px] text-[#7A7369] uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <Sparkles size={11} className="text-[#C24933]" /> Ask next
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {followups.map((q, i) => (
                          <button key={`fu-${i}-${(q || '').slice(0,20)}`} type="button" onClick={() => submitMessage(q)} className="text-xs px-3 py-1.5 rounded-full bg-[#DCF8C6] text-black border border-[#B8E0A0] hover:bg-[#C8F0AE] transition-colors text-left shadow-sm" data-testid={`demo-followup-${i}`}>
                            {q}
                          </button>
                        ))}
                      </div>
                      <button
                        type="button"
                        onClick={async () => {
                          const fb = window.prompt("Suggest an improvement (what should we fix or build?):");
                          const t = (fb || "").trim();
                          if (!t) return;
                          try {
                            await http.post("/feedback", { text: t, source: "demo", page_context: window.location.pathname });
                            toast.success("🙏 Thanks! Your feedback reached the VidyaAI Study team.");
                          } catch (e) { toast.error(formatApiError(e)); }
                        }}
                        className="text-[11px] text-[#7A7369] hover:text-[#C24933] mt-2 underline-offset-2 hover:underline transition-colors"
                        data-testid="demo-feedback-link"
                      >
                        💬 Suggest improvement
                      </button>
                    </div>
                  </div>
                );
              })()}

              {/* Soft conversion nudge — appears once visitor has asked 5 questions.
                  Non-blocking; user can dismiss and keep asking until the hard cap at Q7. */}
              {showSoftPrompt && (
                <div className="rounded-2xl bg-white border border-[#C24933]/20 p-4 sm:p-5 shadow-sm" data-testid="demo-soft-prompt">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#FCE8E2] grid place-items-center shrink-0">
                      <Sparkles size={14} className="text-[#C24933]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-base tracking-tight mb-0.5">Enjoying the tutor?</div>
                      <p className="text-xs text-[#4A4640] mb-3">
                        You have <strong>{remaining} free {remaining === 1 ? "question" : "questions"}</strong> left on this sample. Create a free account now to keep going with <strong>your own notes</strong> — no credit card, no trial expiry.
                      </p>
                      <div className="flex flex-wrap gap-2">
                        <button onClick={() => navigate("/register?role=self_study_student")} className="btn-primary text-xs" data-testid="demo-soft-prompt-signup">
                          Start free <ArrowRight size={12} />
                        </button>
                        <button type="button" onClick={() => setSoftPromptDismissed(true)} className="text-xs px-3 py-1.5 rounded-full bg-[#F4F1EB] border border-[#E6E2D8] text-[#4A4640] hover:bg-white" data-testid="demo-soft-prompt-dismiss">
                          Keep exploring
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Conversion gate after 7 questions */}
              {capReached && (
                <div className="rounded-2xl bg-gradient-to-br from-[#FCE8E2] to-[#FDFBF7] border border-[#C24933]/30 p-5 sm:p-6 text-center" data-testid="demo-cap-gate">
                  <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-[#C24933]/20 text-[#C24933] text-xs font-medium mb-3">
                    <Lock size={12} /> Demo limit reached
                  </div>
                  <div className="font-display text-xl sm:text-2xl tracking-tight mb-2">Liked what you saw?</div>
                  <p className="text-sm text-[#4A4640] mb-5 max-w-md mx-auto">Create a free account to upload <strong>your own</strong> notes, ask unlimited questions, and bring the tutor to Telegram.</p>
                  <div className="flex flex-col sm:flex-row gap-2 justify-center">
                    <button onClick={() => navigate("/register?role=self_study_student")} className="btn-primary" data-testid="demo-cap-signup">
                      Start free <ArrowRight size={14} />
                    </button>
                    <Link to="/" className="btn-ghost" data-testid="demo-cap-home">Back to home</Link>
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={send} className="border-t border-[#E6E2D8] p-4">
              {/* Sample question chips — visible only before any user msg, to nudge first interaction */}
              {messages.filter((m) => m.role === "user").length === 0 && !capReached && (
                <div className="flex flex-wrap gap-2 mb-3" data-testid="demo-sample-chips">
                  {SAMPLE_QUESTIONS.map((q, i) => (
                    <button key={`samp-${i}-${(q || '').slice(0,20)}`} type="button" onClick={() => submitMessage(q)} className="text-xs px-3 py-1.5 rounded-full bg-[#F4F1EB] border border-[#E6E2D8] text-[#4A4640] hover:bg-[#FCE8E2] hover:border-[#C24933]" data-testid={`demo-sample-${i}`}>
                      {q}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex items-end gap-2">
                <textarea
                  ref={textareaRef}
                  value={text}
                  onChange={onTextChange}
                  onKeyDown={onKeyDown}
                  rows={1}
                  disabled={sending || capReached || !session}
                  className="input-field flex-1 resize-none min-h-[44px] max-h-[140px] py-3"
                  placeholder={capReached ? "Sign up to keep asking…" : `Ask anything about ${doc?.title || "the sample chapter"}…`}
                  data-testid="demo-input"
                />
                <button type="submit" disabled={sending || capReached || !text.trim()} className="btn-primary" data-testid="demo-send">
                  <Send size={16} />
                </button>
              </div>
              <div className="mt-1.5 text-[11px] text-[#7A7369]">Enter to send · Shift+Enter for newline</div>
            </form>
          </div>
        </main>
      </div>
    </div>
  );
}
